import React, { FC, useState, useEffect, useRef, ChangeEvent } from 'react';
import useDidUpdate from '../../../../../../hooks/useDidUpdate.effect';
import { documentService } from '../../../../../../utils/window.utils.js';
import Icon from '../../../../../@shared/icon';
import HiglightedOption from '../../../../../@shared/highlightedOption';
import DictionaryTerm from '../../../../../@shared/dictionaryTerm';
import { TProductSelectorDropdown } from '../../../../types/productSelector.types';
import '../../styles.scss';

const ProductSelectorDropdown: FC<TProductSelectorDropdown> = ({
  dropdownOptions = [],
  id,
  handleOptionClick,
  selectedOptionInd: checkedOptInd = 0,
  fields,
  label,
  isEnabled,
  preSelectedOption = '',
  isValidationErrors,
}) => {
  const { field, tracker } = fields;

  const hasAnyError = !!fields?.errors?.length;
  const [isVisible, setIsVisible] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [hasTextInputFocus, setHasTextInputFocus] = useState(false);
  const [initialTextValue, setInitialTextValue] = useState('');
  const [textValue, setTextValue] = useState('');
  const [onSearch, setOnSearch] = useState(false);
  const dropdownRef: React.MutableRefObject<HTMLUListElement | null> = useRef(null);
  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);
  const dropdownValue = preSelectedOption ? preSelectedOption : dropdownOptions?.[checkedOptInd]?.title || '';

  useEffect(() => {
    if (!fields.value && !isValidationErrors) {
      setTextValue('');
      setInitialTextValue('');
      setOnSearch(false);
    }
  }, [fields.value]);

  const onDropdownFocus = (): void => {
    setHasFocus(true);
    setHasTextInputFocus(true);
  };

  const onTextInputBlur = (): void => {
    setHasTextInputFocus(false);
    setOnSearch(false);
  };

  const onDropdownClik = (): void => {
    setIsVisible(!isVisible);

    if (!isVisible) {
      inputRef?.current?.focus();
    } else {
      inputRef?.current?.blur();
    }
  };

  const onSelectOption = (value, text): void => {
    setTextValue(text);
    setInitialTextValue(value);
    setIsVisible(false);
  };

  const onInputUpdate = (e: ChangeEvent<HTMLInputElement>): void => {
    setTextValue(e?.target?.value);
    setOnSearch(true);
  };

  const handleClickOutside = (event): void => {
    if (dropdownRef?.current?.contains(event?.target)) {
      return;
    }

    setIsVisible(false);

    if (textValue === '') {
      setTextValue('');
    } else {
      const hasMatch = dropdownOptions.some((opt) => opt.title === textValue);

      if (!hasMatch) {
        setTextValue(initialTextValue);
      }
    }

    setHasFocus(false);
  };

  useEffect(() => {
    documentService()?.addEventListener('mouseup', handleClickOutside);
    return (): void => {
      documentService()?.removeEventListener('mouseup', handleClickOutside);
    };
  }, [initialTextValue, textValue]); // eslint-disable-line

  useDidUpdate(() => {
    if (hasFocus) {
      tracker?.onFocusField(field, textValue);
    } else {
      tracker?.onBlurField(field, textValue);
    }
  }, [hasFocus]);

  return (
    <>
      <ul className={`form-custom-dropdown ${hasAnyError ? 'form-custom-dropdown--has-error' : ''}`} ref={dropdownRef}>
        <li
          className={`form-custom-dropdown__input-container ${hasFocus ? 'form-custom-dropdown__input-container--has-focus' : ''} ${
            !isEnabled ? 'form-custom-dropdown__input-container--disable' : ''
          }`}
        >
          <label
            htmlFor={id}
            className={`form-custom-dropdown__label ${
              (!hasTextInputFocus && textValue === '' && !hasFocus && dropdownValue === '') || (!hasFocus && dropdownValue ==='')
                ? 'form-custom-dropdown__label--empty-field'
                : ''
            }`}
          >
            <DictionaryTerm keyword={label} />
          </label>
          <input
            className={`form-custom-dropdown__text-input ${!isEnabled ? 'form-custom-dropdown__text-input--disable' : ''}`}
            type="text"
            value={onSearch ? textValue : dropdownValue}
            onChange={onInputUpdate}
            ref={inputRef}
            id={id}
            onClick={onDropdownClik}
            onFocus={onDropdownFocus}
            onBlur={onTextInputBlur}
            autoComplete="off"
            form="novalidatedform"
          />
          {isEnabled && (
            <Icon
              iconName={isVisible ? 'arrow-chevron-up' : 'arrow-chevron-down'}
              size={24}
              extraClassName="form-custom-dropdown__icon"
              color="gray"
              onClick={onDropdownClik}
            />
          )}
        </li>

        <li className={`form-custom-dropdown__list-container ${isVisible ? 'form-custom-dropdown__list-container--visible' : ''}`}>
          <ul className="form-custom-dropdown__list" role="listbox">
            <>
              {dropdownOptions.map((item, index) => (
                <li
                  className="form-custom-dropdown__list-item"
                  role="option" // eslint-disable-line
                  onClick={(): void => {
                    onSelectOption(item.title || '', item?.title || '');
                    handleOptionClick?.(index);
                  }}
                  key={item.id}
                >
                  <HiglightedOption keyword={textValue} optionText={item?.title} key={item.id} />
                </li>
              ))}
            </>
          </ul>
        </li>
      </ul>
    </>
  );
};

export default ProductSelectorDropdown;
